// src/pages/RolesPermissions.js
import React, { useState, useEffect, useMemo } from 'react';
import {
  Box, Container, Paper, Typography, Button, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Dialog, DialogTitle, DialogContent,
  DialogActions, Select, MenuItem, FormControl, InputLabel, Chip,
  Card, CardContent, Alert, Checkbox, TablePagination, TextField,
  CircularProgress, Avatar, IconButton, Tooltip, Fade, Slide,
  useTheme, useMediaQuery, Stack, Divider, Badge, InputAdornment
} from '@mui/material';
import Grid from '@mui/material/GridLegacy';
import { useAuth } from '../context/AuthContext';

import api from '../services/api'; 

import EditIcon from '@mui/icons-material/Edit';
import SearchIcon from '@mui/icons-material/Search';
import PersonIcon from '@mui/icons-material/Person';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import ManageAccountsIcon from '@mui/icons-material/ManageAccounts';
import VisibilityIcon from '@mui/icons-material/Visibility';
import ShieldIcon from '@mui/icons-material/Shield';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import GroupIcon from '@mui/icons-material/Group';
import TuneIcon from '@mui/icons-material/Tune';
import CloseIcon from '@mui/icons-material/Close';
import DownloadIcon from '@mui/icons-material/Download';

// Role Icons and Colors Configuration
const ROLE_CONFIG = {
  admin: {
    icon: <AdminPanelSettingsIcon />,
    color: '#e91e63',
    gradient: 'linear-gradient(135deg, #e91e63 0%, #f06292 100%)',
    bgColor: 'rgba(233, 30, 99, 0.1)'
  },
  manager: {
    icon: <ManageAccountsIcon />,
    color: '#2196f3',
    gradient: 'linear-gradient(135deg, #2196f3 0%, #64b5f6 100%)',
    bgColor: 'rgba(33, 150, 243, 0.1)'
  },
  user: {
    icon: <PersonIcon />,
    color: '#4caf50',
    gradient: 'linear-gradient(135deg, #4caf50 0%, #81c784 100%)',
    bgColor: 'rgba(76, 175, 80, 0.1)'
  },
  client: {
    icon: <VisibilityIcon />,
    color: '#009688',
    gradient: 'linear-gradient(135deg, #009688 0%, #4db6ac 100%)',
    bgColor: 'rgba(0, 150, 136, 0.1)'
  },
  viewer: {
    icon: <VisibilityIcon />,
    color: '#ff9800',
    gradient: 'linear-gradient(135deg, #ff9800 0%, #ffb74d 100%)',
    bgColor: 'rgba(255, 152, 0, 0.1)'
  }
};

const normalizeRoleKey = (rawRole) => {
  const role = String(rawRole || '').toLowerCase();
  return role === 'viewer' ? 'client' : role;
};

const buildPermissionEditor = (roleKey, roleDefinitions, rawOverrides = {}) => {
  const normalizedRole = normalizeRoleKey(roleKey) || 'user';
  const baseModulePermissions = roleDefinitions?.[normalizedRole]?.modulePermissions || {};
  const overrides = rawOverrides?.modulePermissions && typeof rawOverrides.modulePermissions === 'object'
    ? rawOverrides.modulePermissions
    : (rawOverrides && typeof rawOverrides === 'object' ? rawOverrides : {});

  const allModules = new Set([
    ...Object.keys(baseModulePermissions),
    ...Object.keys(overrides)
  ]);

  const editor = {};
  allModules.forEach((moduleKey) => {
    const baseActions = baseModulePermissions[moduleKey] || {};
    const overrideActions = overrides[moduleKey] || {};
    const allActions = new Set([
      ...Object.keys(baseActions),
      ...Object.keys(overrideActions)
    ]);

    const actionMap = {};
    allActions.forEach((actionKey) => {
      if (overrideActions[actionKey] === true) {
        actionMap[actionKey] = 'allow';
      } else if (overrideActions[actionKey] === false) {
        actionMap[actionKey] = 'deny';
      } else {
        actionMap[actionKey] = 'inherit';
      }
    });

    if (Object.keys(actionMap).length) editor[moduleKey] = actionMap;
  });

  return editor;
};

const editorToOverridesPayload = (editor = {}) => {
  const overrides = {};
  Object.entries(editor).forEach(([moduleKey, actions]) => {
    if (!actions || typeof actions !== 'object') return;
    Object.entries(actions).forEach(([actionKey, value]) => {
      if (value !== 'allow' && value !== 'deny') return;
      if (!overrides[moduleKey]) overrides[moduleKey] = {};
      overrides[moduleKey][actionKey] = value === 'allow';
    });
  });
  return overrides;
};

const hasAnyOverrideInEditor = (editor = {}) => {
  return Object.keys(editorToOverridesPayload(editor)).length > 0;
};

const BILLING_SCOPE_TYPES = ['invoice', 'quote', 'purchase_order'];
const BILLING_SCOPE_LABELS = {
  invoice: 'Invoice',
  quote: 'Quote',
  purchase_order: 'Purchase Order'
};

const buildBillingScopeEditor = (roleKey, roleDefinitions, rawOverrides = {}) => {
  const normalizedRole = normalizeRoleKey(roleKey) || 'user';
  const baseScopes = roleDefinitions?.[normalizedRole]?.billingDocumentScopes || {};
  const scopeOverrides = rawOverrides?.billingDocumentScopeOverrides && typeof rawOverrides.billingDocumentScopeOverrides === 'object'
    ? rawOverrides.billingDocumentScopeOverrides
    : (rawOverrides?.billingDocumentScopes && typeof rawOverrides.billingDocumentScopes === 'object'
      ? rawOverrides.billingDocumentScopes
      : {});

  const editor = {};
  BILLING_SCOPE_TYPES.forEach((typeKey) => {
    const baseScope = String(baseScopes[typeKey] || 'own').toLowerCase();
    const overrideScope = String(scopeOverrides[typeKey] || 'inherit').toLowerCase();
    editor[typeKey] = ['all', 'own', 'none', 'inherit'].includes(overrideScope)
      ? overrideScope
      : (['all', 'own', 'none'].includes(baseScope) ? 'inherit' : 'inherit');
  });
  return editor;
};

const billingScopeEditorToOverridesPayload = (editor = {}) => {
  const overrides = {};
  BILLING_SCOPE_TYPES.forEach((typeKey) => {
    const scope = String(editor[typeKey] || 'inherit').toLowerCase();
    if (scope === 'all' || scope === 'own' || scope === 'none') {
      overrides[typeKey] = scope;
    }
  });
  return overrides;
};

const hasAnyBillingScopeOverride = (editor = {}) => Object.keys(billingScopeEditorToOverridesPayload(editor)).length > 0;

const RolesPermissions = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  // eslint-disable-next-line no-unused-vars
  const isTablet = useMediaQuery(theme.breakpoints.down('lg'));
  const { user, hasPermission, ROLE_HIERARCHY, updateUserRole } = useAuth();
  
  const [users, setUsers] = useState([]);
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [newRole, setNewRole] = useState('');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [bulkRole, setBulkRole] = useState('');
  const [rolePermissions, setRolePermissions] = useState({});
  const [roleDefinitions, setRoleDefinitions] = useState({});
  const [permissionEditor, setPermissionEditor] = useState({});
  const [initialPermissionEditor, setInitialPermissionEditor] = useState({});
  const [billingScopeEditor, setBillingScopeEditor] = useState({});
  const [initialBillingScopeEditor, setInitialBillingScopeEditor] = useState({});
  const [overridesLoading, setOverridesLoading] = useState(false);

  const roleChanged = useMemo(
    () => normalizeRoleKey(newRole) !== normalizeRoleKey(selectedUser?.role),
    [newRole, selectedUser]
  );

  const currentOverridePayload = useMemo(
    () => editorToOverridesPayload(permissionEditor),
    [permissionEditor]
  );

  const initialOverridePayload = useMemo(
    () => editorToOverridesPayload(initialPermissionEditor),
    [initialPermissionEditor]
  );

  const overridesChanged = useMemo(
    () => JSON.stringify(currentOverridePayload) !== JSON.stringify(initialOverridePayload),
    [currentOverridePayload, initialOverridePayload]
  );

  const currentBillingScopePayload = useMemo(
    () => billingScopeEditorToOverridesPayload(billingScopeEditor),
    [billingScopeEditor]
  );

  const initialBillingScopePayload = useMemo(
    () => billingScopeEditorToOverridesPayload(initialBillingScopeEditor),
    [initialBillingScopeEditor]
  );

  const billingScopesChanged = useMemo(
    () => JSON.stringify(currentBillingScopePayload) !== JSON.stringify(initialBillingScopePayload),
    [currentBillingScopePayload, initialBillingScopePayload]
  );

  useEffect(() => {
    if (user && hasPermission && hasPermission('manage_users')) {
      fetchUsers();
      fetchPermissions();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, hasPermission]);

  useEffect(() => {
    if (!openDialog || !selectedUser) return;
    if (!Object.keys(roleDefinitions || {}).length) return;
    if (overridesLoading) return;
    if (Object.keys(permissionEditor || {}).length) return;
    loadUserOverrides(selectedUser, newRole || selectedUser.role);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openDialog, selectedUser, roleDefinitions, overridesLoading, permissionEditor, newRole]);

  const fetchUsers = async () => {
    setLoading(true);
    try {
      // ✅ USAMOS api.get (Axios maneja la URL base y el token)
      const response = await api.get('/roles/users');
      setUsers(response.data.users || []);
    } catch (error) {
      console.error('Error fetching users:', error);
      const errorMsg = error.response?.data?.message || error.message;
      setMessage(`Error loading users: ${errorMsg}`);
    } finally {
      setLoading(false);
    }
  };

  const fetchPermissions = async () => {
    try {
      // ✅ USAMOS api.get
      const response = await api.get('/roles/permissions');
      setRolePermissions(response.data.rolePermissions || {});
      setRoleDefinitions(response.data.roleDefinitions || {});
    } catch (error) {
      console.error('Error loading permissions:', error);
      // No seteamos mensaje de error global para no interrumpir la UI si esto falla
    }
  };

  const loadUserOverrides = async (userItem, roleValue) => {
    if (!userItem?.id) return;
    setOverridesLoading(true);
    try {
      const response = await api.get(`/roles/users/${userItem.id}/permission-overrides`);
      const overrides = response?.data?.overrides || {};
      const roleKey = normalizeRoleKey(roleValue || response?.data?.role || userItem.role);
      const editor = buildPermissionEditor(roleKey, roleDefinitions, overrides);
      const scopesEditor = buildBillingScopeEditor(roleKey, roleDefinitions, {
        billingDocumentScopeOverrides: response?.data?.billingDocumentScopeOverrides || {}
      });
      setPermissionEditor(editor);
      setInitialPermissionEditor(editor);
      setBillingScopeEditor(scopesEditor);
      setInitialBillingScopeEditor(scopesEditor);
    } catch (error) {
      console.error('Error loading user overrides:', error);
      const roleKey = normalizeRoleKey(roleValue || userItem.role);
      const emptyEditor = buildPermissionEditor(roleKey, roleDefinitions, {});
      const emptyScopesEditor = buildBillingScopeEditor(roleKey, roleDefinitions, {});
      setPermissionEditor(emptyEditor);
      setInitialPermissionEditor(emptyEditor);
      setBillingScopeEditor(emptyScopesEditor);
      setInitialBillingScopeEditor(emptyScopesEditor);
      setMessage(`❌ Error loading custom permissions: ${error.response?.data?.message || error.message}`);
    } finally {
      setOverridesLoading(false);
    }
  };

  const handleOpenDialog = (userItem) => {
    setSelectedUser(userItem);
    setNewRole(normalizeRoleKey(userItem.role));
    setOpenDialog(true);
    loadUserOverrides(userItem, userItem.role);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSelectedUser(null);
    setNewRole('');
    setPermissionEditor({});
    setInitialPermissionEditor({});
    setBillingScopeEditor({});
    setInitialBillingScopeEditor({});
    setOverridesLoading(false);
  };

  const rebuildEditorForRole = (roleValue, baseEditor = permissionEditor) => {
    const existingOverrides = editorToOverridesPayload(baseEditor);
    return buildPermissionEditor(roleValue, roleDefinitions, existingOverrides);
  };

  const rebuildBillingScopeEditorForRole = (roleValue, baseEditor = billingScopeEditor) => {
    const existingOverrides = billingScopeEditorToOverridesPayload(baseEditor);
    return buildBillingScopeEditor(roleValue, roleDefinitions, { billingDocumentScopes: existingOverrides });
  };

  const handleRoleChange = (nextRoleRaw) => {
    const nextRole = normalizeRoleKey(nextRoleRaw);
    setNewRole(nextRole);
    setPermissionEditor((prev) => rebuildEditorForRole(nextRole, prev));
    setBillingScopeEditor((prev) => rebuildBillingScopeEditorForRole(nextRole, prev));
  };

  const updatePermissionEditorValue = (moduleKey, actionKey, nextValue) => {
    setPermissionEditor((prev) => ({
      ...prev,
      [moduleKey]: {
        ...(prev[moduleKey] || {}),
        [actionKey]: nextValue
      }
    }));
  };

  const resetPermissionOverrides = () => {
    const nextEditor = buildPermissionEditor(newRole, roleDefinitions, {});
    setPermissionEditor(nextEditor);
    const nextScopeEditor = buildBillingScopeEditor(newRole, roleDefinitions, {});
    setBillingScopeEditor(nextScopeEditor);
  };

  const handleUpdateRole = async () => {
    if (!selectedUser || !newRole) return;
    if (!roleChanged && !overridesChanged && !billingScopesChanged) {
      setMessage('No changes to save');
      return;
    }

    setLoading(true);
    try {
      if (roleChanged) {
        await updateUserRole(selectedUser.id, newRole);
      }

      if (overridesChanged || billingScopesChanged) {
        if (Object.keys(currentOverridePayload).length === 0 && Object.keys(currentBillingScopePayload).length === 0) {
          await api.delete(`/roles/users/${selectedUser.id}/permission-overrides`);
        } else {
          await api.put(`/roles/users/${selectedUser.id}/permission-overrides`, {
            overrides: currentOverridePayload,
            billingDocumentScopes: currentBillingScopePayload
          });
        }
      }

      const resultSummary = [
        roleChanged ? 'role' : null,
        (overridesChanged || billingScopesChanged) ? 'custom permissions' : null
      ].filter(Boolean).join(' + ');

      setMessage(`✅ Updated ${resultSummary} successfully`);
      fetchUsers();
      handleCloseDialog();
      setTimeout(() => setMessage(''), 3000);
    } catch (error) {
      setMessage(`❌ ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleBulkUpdate = async () => {
    if (selectedUsers.length === 0 || !bulkRole) {
      setMessage('Select users and a role');
      return;
    }

    setLoading(true);
    try {
      // ✅ USAMOS api.put. Enviamos el objeto directo, Axios lo stringify
      const response = await api.put('/roles/users/bulk', {
        userIds: selectedUsers,
        role: bulkRole
      });

      setMessage(`✅ ${response.data.message}`);
      fetchUsers();
      setSelectedUsers([]);
      setBulkRole('');
      setTimeout(() => setMessage(''), 3000);
    } catch (error) {
      const errorMsg = error.response?.data?.error || error.message;
      setMessage(`❌ ${errorMsg}`);
    } finally {
      setLoading(false);
    }
  };

  const toggleUserSelection = (userId) => {
    setSelectedUsers(prev =>
      prev.includes(userId)
        ? prev.filter(id => id !== userId)
        : [...prev, userId]
    );
  };

  const filteredUsers = users.filter(u =>
    u.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    u.email?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleExportAccessReview = () => {
    const canonicalRoleKeys = ['admin', 'manager', 'user', 'client'];
    const canonicalRoleModel = canonicalRoleKeys.reduce((acc, roleKey) => {
      const definition = roleDefinitions?.[roleKey];
      if (!definition) return acc;
      acc[roleKey] = {
        level: definition.level,
        module_permissions: definition.modulePermissions || {},
        billing_document_scopes: definition.billingDocumentScopes || {}
      };
      return acc;
    }, {});
    const accountInventory = users.map((account) => ({
      user_id: account.id,
      personalized_user_id: account.username,
      role: normalizeRoleKey(account.role),
      status: Number(account.is_active) === 1 ? 'active' : 'inactive',
      custom_permissions: Number(account.has_custom_permissions) === 1,
      created_at: account.created_at || null
    }));
    const payload = {
      report: 'SMTLINKS Access Review Evidence',
      generated_at: new Date().toISOString(),
      generated_by: {
        user_id: user.id,
        personalized_user_id: user.username,
        role: user.role
      },
      summary: {
        total_accounts: accountInventory.length,
        active_accounts: accountInventory.filter((account) => account.status === 'active').length,
        inactive_accounts: accountInventory.filter((account) => account.status === 'inactive').length,
        accounts_with_custom_permissions: accountInventory.filter((account) => account.custom_permissions).length
      },
      accounts: accountInventory,
      canonical_role_model: canonicalRoleModel,
      qualification: 'Management must review each active account and retain approval or remediation evidence.'
    };

    const blob = new Blob([JSON.stringify(payload, null, 2)], { type: 'application/json;charset=utf-8;' });
    const objectUrl = URL.createObjectURL(blob);
    const link = document.createElement('a');
    const dateStamp = new Date().toISOString().slice(0, 10).replace(/-/g, '');
    link.href = objectUrl;
    link.download = `access_review_${dateStamp}.json`;
    link.click();
    URL.revokeObjectURL(objectUrl);
    setMessage('Access review evidence exported successfully');
    setTimeout(() => setMessage(''), 3000);
  };

  // Validations
  if (!user) {
    return (
      <Container maxWidth="lg" sx={{ py: 4, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress />
      </Container>
    );
  }

  const userRole = user.role?.toLowerCase();
  const isAdmin = userRole === 'admin';
  const hasManageUsersPermission = hasPermission?.('manage_users');

  if (!isAdmin && !hasManageUsersPermission) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Alert severity="error" sx={{ borderRadius: 3 }}>
          <Typography variant="h6" sx={{ mb: 1 }}>
            ❌ Access Denied
          </Typography>
          <Typography variant="body2" sx={{ mb: 1 }}>
            You do not have permission to manage user roles and permissions.
          </Typography>
          <Typography variant="body2" color="text.secondary">
            <strong>Your current role:</strong> {user.role || 'Not detected'}
          </Typography>
          <Box sx={{ mt: 2 }}>
            <Button variant="contained" href="/dashboard">
              Back to Dashboard
            </Button>
          </Box>
        </Alert>
      </Container>
    );
  }

  return (
    <Box sx={{ width: '100%' }}>
      {/* Header */}
      <Fade in timeout={500}>
        <Paper elevation={0} sx={{ p: { xs: 1.35, md: 1.6 }, mb: 1.5, borderRadius: 2.5, color: 'white', background: 'linear-gradient(135deg, #667eea 0%, #124791 100%)' }}>
          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: 1.1,
            flexWrap: 'wrap'
          }}>
            <Avatar
              sx={{
                bgcolor: 'rgba(255,255,255,.16)',
                width: 42,
                height: 42
              }}
            >
              <ShieldIcon sx={{ fontSize: 24 }} />
            </Avatar>
            <Box sx={{ flex: 1 }}>
              <Typography 
                variant="h5"
                sx={{ fontWeight: 800, mb: 0.25 }}
              >
                Roles & Permissions
              </Typography>
              <Typography variant="body2" sx={{ color: 'rgba(255,255,255,.82)' }}>
                Manage roles and permissions for system users
              </Typography>
            </Box>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ xs: 'stretch', sm: 'center' }}>
              <Chip
                icon={<GroupIcon />}
                label={`${users.length} users`}
                sx={{ bgcolor: 'rgba(255,255,255,.13)', color: 'white' }}
              />
              <Button
                variant="outlined"
                startIcon={<DownloadIcon />}
                onClick={handleExportAccessReview}
                disabled={!users.length || !Object.keys(roleDefinitions || {}).length}
                size="small"
                sx={{ textTransform: 'none', fontWeight: 700, color: 'white', borderColor: 'rgba(255,255,255,.45)' }}
              >
                Export Access Review
              </Button>
            </Stack>
          </Box>
        </Paper>
      </Fade>

      {/* Success/Error Message */}
      {message && (
        <Fade in>
          <Alert 
            severity={message.includes('❌') ? 'error' : 'success'} 
            sx={{ mb: 3, borderRadius: 2 }}
            onClose={() => setMessage('')}
          >
            {message}
          </Alert>
        </Fade>
      )}

      {/* Role Cards */}
      {ROLE_HIERARCHY && Object.keys(ROLE_HIERARCHY).length > 0 && (
        <Grid container spacing={1.2} sx={{ mb: 1.5 }}>
          {Object.entries(ROLE_HIERARCHY).map(([roleKey, roleData], index) => {
            const config = ROLE_CONFIG[roleKey] || ROLE_CONFIG.user;
            return (
              <Grid item xs={12} sm={6} lg={3} key={roleKey}>
                <Fade in timeout={500 + index * 100}>
                  <Card
                    sx={{
                      background: config.gradient,
                      color: 'white',
                      position: 'relative',
                      overflow: 'hidden',
                      transition: 'transform 0.2s, box-shadow 0.2s',
                      '&:hover': {
                        transform: 'translateY(-2px)',
                        boxShadow: 6
                      }
                    }}
                  >
                    <CardContent sx={{ p: 1.4, '&:last-child': { pb: 1.4 } }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                        <Avatar
                          sx={{
                            bgcolor: 'rgba(255,255,255,0.3)',
                            width: 36,
                            height: 36,
                            mr: 1
                          }}
                        >
                          {config.icon}
                        </Avatar>
                        <Box sx={{ flex: 1 }}>
                          <Typography variant="h6" sx={{ fontWeight: 700 }}>
                            {roleData.label}
                          </Typography>
                          <Typography variant="caption" sx={{ opacity: 0.9 }}>
                            Level {roleData.level}
                          </Typography>
                        </Box>
                      </Box>
                      
                      <Divider sx={{ my: 1, borderColor: 'rgba(255,255,255,0.3)' }} />
                      
                      <Typography variant="caption" sx={{ display: 'block', mb: 1, opacity: 0.9 }}>
                        {roleData.permissions?.length || 0} permissions
                      </Typography>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {roleData.permissions?.slice(0, 3).map(perm => (
                          <Chip
                            key={perm}
                            label={perm}
                            size="small"
                            sx={{
                              bgcolor: 'rgba(255,255,255,0.2)',
                              color: 'white',
                              fontSize: '0.7rem',
                              height: 24
                            }}
                          />
                        ))}
                        {roleData.permissions?.length > 3 && (
                          <Chip
                            label={`+${roleData.permissions.length - 3}`}
                            size="small"
                            sx={{
                              bgcolor: 'rgba(255,255,255,0.3)',
                              color: 'white',
                              fontSize: '0.7rem',
                              height: 24
                            }}
                          />
                        )}
                      </Box>
                    </CardContent>
                    
                    {/* Decoration */}
                    <Box
                      sx={{
                        position: 'absolute',
                        top: -20,
                        right: -20,
                        width: 100,
                        height: 100,
                        borderRadius: '50%',
                        bgcolor: 'rgba(255,255,255,0.1)',
                      }}
                    />
                  </Card>
                </Fade>
              </Grid>
            );
          })}
        </Grid>
      )}

      {/* Bulk Actions */}
      {selectedUsers.length > 0 && (
        <Slide direction="down" in mountOnEnter unmountOnExit>
          <Paper
            elevation={3}
            sx={{
              p: 2,
              mb: 3,
              borderRadius: 3,
              background: theme.palette.mode === 'dark' 
                ? 'linear-gradient(135deg, #1e3c72 0%, #2a5298 100%)'
                : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              color: 'white'
            }}
          >
            <Stack 
              direction={{ xs: 'column', sm: 'row' }} 
              spacing={2} 
              alignItems={{ xs: 'stretch', sm: 'center' }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Badge badgeContent={selectedUsers.length} color="error">
                  <GroupIcon />
                </Badge>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {selectedUsers.length} selected
                </Typography>
              </Box>
              
              <FormControl size="small" sx={{ minWidth: { xs: 0, sm: 150 }, width: { xs: '100%', sm: 'auto' }, flex: 1 }}>
                <InputLabel sx={{ color: 'rgba(255,255,255,0.8)' }}>New Role</InputLabel>
                <Select
                  value={bulkRole}
                  label="New Role"
                  onChange={(e) => setBulkRole(e.target.value)}
                  sx={{
                    bgcolor: 'rgba(255,255,255,0.15)',
                    color: 'white',
                    '.MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,0.3)' },
                    '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,0.5)' }
                  }}
                >
                  {ROLE_HIERARCHY && Object.entries(ROLE_HIERARCHY).map(([key, val]) => (
                    <MenuItem key={key} value={key}>{val.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              
              <Button
                variant="contained"
                onClick={handleBulkUpdate}
                disabled={loading || !bulkRole}
                sx={{
                  bgcolor: 'white',
                  color: 'primary.main',
                  '&:hover': { bgcolor: 'rgba(255,255,255,0.9)' }
                }}
                startIcon={<CheckCircleIcon />}
              >
                Apply
              </Button>
              
              <IconButton
                onClick={() => setSelectedUsers([])}
                sx={{ color: 'white' }}
              >
                <CloseIcon />
              </IconButton>
            </Stack>
          </Paper>
        </Slide>
      )}

      {/* Search */}
      <Paper elevation={0} sx={{ p: 1.3, mb: 1.5, borderRadius: 2.5, border: '1px solid', borderColor: 'divider' }}>
        <TextField
          fullWidth
          placeholder="Search by name or email..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon color="action" />
              </InputAdornment>
            ),
            endAdornment: searchTerm && (
              <InputAdornment position="end">
                <IconButton size="small" onClick={() => setSearchTerm('')}>
                  <CloseIcon fontSize="small" />
                </IconButton>
              </InputAdornment>
            )
          }}
          sx={{
            '& .MuiOutlinedInput-root': {
              borderRadius: 2
            }
          }}
        />
      </Paper>

      {/* Table / Mobile View */}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 6 }}>
          <CircularProgress size={60} />
        </Box>
      ) : isMobile ? (
        // Mobile View with Cards
        <Box sx={{ mb: 3 }}>
          {filteredUsers
            .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
            .map((userItem, index) => {
              const config = ROLE_CONFIG[userItem.role] || ROLE_CONFIG.user;
              return (
                <Fade in timeout={300 + index * 50} key={userItem.id}>
                  <Card
                    sx={{
                      mb: 2,
                      borderRadius: 3,
                      border: '1px solid',
                      borderColor: 'divider',
                      transition: 'transform 0.2s, box-shadow 0.2s',
                      '&:active': {
                        transform: 'scale(0.98)',
                        boxShadow: 3
                      }
                    }}
                  >
                    <CardContent>
                      <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 2 }}>
                        <Checkbox
                          checked={selectedUsers.includes(userItem.id)}
                          onChange={() => toggleUserSelection(userItem.id)}
                          disabled={userItem.id === user?.id}
                        />
                        <Avatar
                          sx={{
                            bgcolor: config.bgColor,
                            color: config.color,
                            width: 48,
                            height: 48,
                            mr: 2
                          }}
                        >
                          {config.icon}
                        </Avatar>
                        <Box sx={{ flex: 1 }}>
                          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                            {userItem.name}
                          </Typography>
                          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                            {userItem.email}
                          </Typography>
                          <Chip
                            label={ROLE_HIERARCHY?.[userItem.role]?.label || userItem.role}
                            size="small"
                            sx={{
                              bgcolor: config.bgColor,
                              color: config.color,
                              fontWeight: 600
                            }}
                          />
                          {Number(userItem.has_custom_permissions) === 1 && (
                            <Chip
                              label="Custom"
                              size="small"
                              color="secondary"
                              variant="outlined"
                            />
                          )}
                        </Box>
                      </Box>
                      
                      <Divider sx={{ my: 1.5 }} />
                      
                      <Box sx={{ mb: 1.5 }}>
                        <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>
                          Permissions:
                        </Typography>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                          {rolePermissions[userItem.role]?.slice(0, 3).map(perm => (
                            <Chip key={perm} label={perm} size="small" variant="outlined" />
                          ))}
                          {rolePermissions[userItem.role]?.length > 3 && (
                            <Chip 
                              label={`+${rolePermissions[userItem.role].length - 3}`} 
                              size="small" 
                              variant="outlined" 
                            />
                          )}
                        </Box>
                      </Box>
                      
                      {userItem.id !== user?.id && (
                        <Button
                          fullWidth
                          variant="outlined"
                          size="small"
                          startIcon={<EditIcon />}
                          onClick={() => handleOpenDialog(userItem)}
                        >
                          Edit Role
                        </Button>
                      )}
                    </CardContent>
                  </Card>
                </Fade>
              );
            })}
        </Box>
      ) : (
        // Desktop View with Table
        <Paper elevation={0} sx={{ borderRadius: 3, overflow: 'hidden', border: '1px solid', borderColor: 'divider' }}>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow sx={{ bgcolor: 'action.hover' }}>
                  <TableCell padding="checkbox">
                    <Checkbox
                      checked={selectedUsers.length === filteredUsers.length && filteredUsers.length > 0}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setSelectedUsers(filteredUsers.map(u => u.id));
                        } else {
                          setSelectedUsers([]);
                        }
                      }}
                    />
                  </TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>User</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Role</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Permissions</TableCell>
                  <TableCell sx={{ fontWeight: 700 }} align="center">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredUsers
                  .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                  .map((userItem, index) => {
                    const config = ROLE_CONFIG[userItem.role] || ROLE_CONFIG.user;
                    return (
                      <Fade in timeout={200 + index * 50} key={userItem.id}>
                        <TableRow
                          hover
                          sx={{
                            '&:last-child td': { border: 0 },
                            transition: 'background-color 0.2s'
                          }}
                        >
                          <TableCell padding="checkbox">
                            <Checkbox
                              checked={selectedUsers.includes(userItem.id)}
                              onChange={() => toggleUserSelection(userItem.id)}
                              disabled={userItem.id === user?.id}
                            />
                          </TableCell>
                          <TableCell>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                              <Avatar
                                sx={{
                                  bgcolor: config.bgColor,
                                  color: config.color,
                                  width: 40,
                                  height: 40
                                }}
                              >
                                {config.icon}
                              </Avatar>
                              <Box>
                                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                  {userItem.name}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                  {userItem.email}
                                </Typography>
                              </Box>
                            </Box>
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={ROLE_HIERARCHY?.[userItem.role]?.label || userItem.role}
                              size="small"
                              sx={{
                                bgcolor: config.bgColor,
                                color: config.color,
                                fontWeight: 600
                              }}
                            />
                            {Number(userItem.has_custom_permissions) === 1 && (
                              <Chip
                                label="Custom"
                                size="small"
                                color="secondary"
                                variant="outlined"
                                sx={{ ml: 1 }}
                              />
                            )}
                          </TableCell>
                          <TableCell>
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, maxWidth: 300 }}>
                              {rolePermissions[userItem.role]?.slice(0, 2).map(perm => (
                                <Chip key={perm} label={perm} size="small" variant="outlined" />
                              ))}
                              {rolePermissions[userItem.role]?.length > 2 && (
                                <Tooltip 
                                  title={rolePermissions[userItem.role].slice(2).join(', ')}
                                  arrow
                                >
                                  <Chip 
                                    label={`+${rolePermissions[userItem.role].length - 2}`} 
                                    size="small" 
                                    variant="outlined" 
                                    sx={{ cursor: 'help' }}
                                  />
                                </Tooltip>
                              )}
                            </Box>
                          </TableCell>
                          <TableCell align="center">
                            {userItem.id !== user?.id && (
                              <Tooltip title="Edit user role">
                                <IconButton
                                  size="small"
                                  onClick={() => handleOpenDialog(userItem)}
                                  sx={{
                                    color: config.color,
                                    '&:hover': { bgcolor: config.bgColor }
                                  }}
                                >
                                  <EditIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            )}
                          </TableCell>
                        </TableRow>
                      </Fade>
                    );
                  })}
              </TableBody>
            </Table>
          </TableContainer>

          <Divider />

          <TablePagination
            rowsPerPageOptions={[5, 10, 25]}
            component="div"
            count={filteredUsers.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={(e, newPage) => setPage(newPage)}
            onRowsPerPageChange={(e) => {
              setRowsPerPage(parseInt(e.target.value, 10));
              setPage(0);
            }}
            labelRowsPerPage={isMobile ? 'Per page:' : 'Rows per page:'}
          />
        </Paper>
      )}

      {/* Edit Dialog */}
      <Dialog 
        open={openDialog} 
        onClose={handleCloseDialog} 
        maxWidth="sm" 
        fullWidth
        fullScreen={isMobile}
        TransitionComponent={Slide}
        TransitionProps={{ direction: 'up' }}
        PaperProps={{
          sx: { borderRadius: isMobile ? 0 : 3 }
        }}
      >
        <DialogTitle sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          gap: 2, 
          borderBottom: '1px solid', 
          borderColor: 'divider'
        }}>
          <Avatar sx={{ bgcolor: 'primary.main' }}>
            <TuneIcon />
          </Avatar>
          <Box sx={{ flex: 1 }}>
            <Typography variant="h6">Change User Role</Typography>
            <Typography variant="caption" color="text.secondary">
              Select the new role to assign
            </Typography>
          </Box>
          {isMobile && (
            <IconButton onClick={handleCloseDialog}>
              <CloseIcon />
            </IconButton>
          )}
        </DialogTitle>
        
        <DialogContent sx={{ pt: 3 }}>
          <Box sx={{ 
            p: 2, 
            mb: 3, 
            borderRadius: 2, 
            bgcolor: 'action.hover',
            display: 'flex', 
            alignItems: 'center', 
            gap: 2
          }}>
            <Avatar sx={{ bgcolor: 'primary.main' }}>
              {selectedUser?.name?.charAt(0)}
            </Avatar>
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                {selectedUser?.name}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ overflowWrap: 'anywhere' }}>
                {selectedUser?.email}
              </Typography>
            </Box>
          </Box>

          <FormControl fullWidth>
            <InputLabel>New Role</InputLabel>
            <Select
              value={newRole}
              label="New Role"
              onChange={(e) => handleRoleChange(e.target.value)}
            >
              {ROLE_HIERARCHY && Object.entries(ROLE_HIERARCHY).map(([roleKey, roleData]) => {
                const config = ROLE_CONFIG[roleKey] || ROLE_CONFIG.user;
                return (
                  <MenuItem key={roleKey} value={roleKey}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                      <Avatar sx={{ 
                        bgcolor: config.bgColor, 
                        color: config.color, 
                        width: 32, 
                        height: 32
                      }}>
                        {config.icon}
                      </Avatar>
                      <Box>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          {roleData.label}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Level {roleData.level} - {roleData.permissions?.length || 0} permissions
                        </Typography>
                      </Box>
                    </Box>
                  </MenuItem>
                );
              })}
            </Select>
          </FormControl>

          <Box sx={{ mt: 3 }}>
            <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, justifyContent: 'space-between', alignItems: { xs: 'stretch', sm: 'center' }, gap: 1, mb: 1 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                Custom Permissions (user-level)
              </Typography>
              <Button
                size="small"
                variant="text"
                onClick={resetPermissionOverrides}
                disabled={overridesLoading || (!hasAnyOverrideInEditor(permissionEditor) && !hasAnyBillingScopeOverride(billingScopeEditor))}
              >
                Reset to role defaults
              </Button>
            </Box>
            <Typography variant="caption" color="text.secondary" sx={{ mb: 2, display: 'block' }}>
              Admin only. You can override what this user can view/action without changing role.
            </Typography>

            <Paper variant="outlined" sx={{ p: 1.5, mb: 1.5, borderRadius: 2 }}>
              <Typography variant="subtitle2" sx={{ mb: 1.2 }}>
                Billing Documents Scope
              </Typography>
              <Stack spacing={1}>
                {BILLING_SCOPE_TYPES.map((typeKey) => {
                  const roleBaseScope = String(
                    roleDefinitions?.[normalizeRoleKey(newRole)]?.billingDocumentScopes?.[typeKey] || 'own'
                  ).toLowerCase();

                  return (
                    <Box
                      key={`scope-${typeKey}`}
                      sx={{
                        display: 'flex',
                        flexDirection: { xs: 'column', sm: 'row' },
                        gap: 1,
                        alignItems: { xs: 'stretch', sm: 'center' },
                        justifyContent: 'space-between'
                      }}
                    >
                      <Typography variant="body2">{BILLING_SCOPE_LABELS[typeKey]}</Typography>
                      <FormControl size="small" sx={{ minWidth: { xs: 0, sm: 220 }, width: { xs: '100%', sm: 'auto' } }}>
                        <Select
                          value={billingScopeEditor[typeKey] || 'inherit'}
                          onChange={(e) => setBillingScopeEditor((prev) => ({ ...prev, [typeKey]: e.target.value }))}
                        >
                          <MenuItem value="inherit">Inherit role ({roleBaseScope})</MenuItem>
                          <MenuItem value="all">All documents</MenuItem>
                          <MenuItem value="own">Only own documents</MenuItem>
                          <MenuItem value="none">No access</MenuItem>
                        </Select>
                      </FormControl>
                    </Box>
                  );
                })}
              </Stack>
            </Paper>

            {overridesLoading ? (
              <Box sx={{ py: 3, textAlign: 'center' }}>
                <CircularProgress size={24} />
              </Box>
            ) : (
              <Box sx={{ maxHeight: 320, overflowY: 'auto', pr: 0.5 }}>
                {Object.entries(permissionEditor).map(([moduleKey, actions]) => (
                  <Paper
                    key={moduleKey}
                    variant="outlined"
                    sx={{ p: 1.5, mb: 1.25, borderRadius: 2 }}
                  >
                    <Typography variant="subtitle2" sx={{ textTransform: 'capitalize', mb: 1 }}>
                      {moduleKey.replace(/([A-Z])/g, ' $1').trim()}
                    </Typography>
                    <Stack spacing={1}>
                      {Object.keys(actions).map((actionKey) => {
                        const roleBaseAllowed = Boolean(
                          roleDefinitions?.[normalizeRoleKey(newRole)]?.modulePermissions?.[moduleKey]?.[actionKey]
                        );

                        return (
                          <Box
                            key={`${moduleKey}-${actionKey}`}
                            sx={{
                              display: 'flex',
                              flexDirection: { xs: 'column', sm: 'row' },
                              gap: 1,
                              alignItems: { xs: 'stretch', sm: 'center' },
                              justifyContent: 'space-between'
                            }}
                          >
                            <Typography variant="body2" sx={{ textTransform: 'capitalize' }}>
                              {actionKey}
                            </Typography>
                            <FormControl size="small" sx={{ minWidth: { xs: 0, sm: 190 }, width: { xs: '100%', sm: 'auto' } }}>
                              <Select
                                value={actions[actionKey]}
                                onChange={(e) => updatePermissionEditorValue(moduleKey, actionKey, e.target.value)}
                              >
                                <MenuItem value="inherit">
                                  Inherit role ({roleBaseAllowed ? 'Allow' : 'Hide'})
                                </MenuItem>
                                <MenuItem value="allow">Allow</MenuItem>
                                <MenuItem value="deny">Hide</MenuItem>
                              </Select>
                            </FormControl>
                          </Box>
                        );
                      })}
                    </Stack>
                  </Paper>
                ))}
              </Box>
            )}
          </Box>
        </DialogContent>
        
        <DialogActions sx={{ p: 2, borderTop: '1px solid', borderColor: 'divider', flexDirection: { xs: 'column-reverse', sm: 'row' }, alignItems: { xs: 'stretch', sm: 'center' } }}>
          <Button onClick={handleCloseDialog} disabled={loading}>
            Cancel
          </Button>
          <Button
            onClick={handleUpdateRole}
            variant="contained"
            disabled={loading || (!roleChanged && !overridesChanged && !billingScopesChanged)}
            startIcon={loading ? <CircularProgress size={20} /> : <CheckCircleIcon />}
          >
            {loading ? 'Saving...' : 'Save Changes'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default RolesPermissions;

